import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setSelectedChat } from "../../lib/slices/selectedChatSlice";
import { getAllConversation } from "../../services/commonApiFn";
import { handleOpenSnackBar } from "../../utils";
import appcolors from "./../../constants/Colors";

function CompanyLists({ socket }) {
  const { t } = useTranslation(["common"]);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [allCompanies, setAllCompanies] = useState([]);
  const active = useSelector((state) => state.activeChat.selectedChat);
  const userData = useSelector((state) => state.userData.user);

  useEffect(() => {
    fetchAllCompanies();
  }, []);

  useEffect(() => {
    socket.emit("connectWithAdmin", { room: "adminRoom" });

    // Handle new conversation
    socket.on("newConversation", (newConversation) => {
      setAllCompanies((prev) => {
        const updatedIndex = prev.findIndex(
          (company) =>
            company.conversationId === newConversation.conversationId,
        );

        if (updatedIndex > -1) {
          // Update existing conversation
          const updatedCompanies = [...prev];
          updatedCompanies[updatedIndex].lastMessage =
            newConversation.lastMessage;
          updatedCompanies[updatedIndex].lastUpdated =
            newConversation.lastUpdated;
          updatedCompanies[updatedIndex].unreadMessagesCount =
            newConversation.unreadMessagesCount;

          // Move updated conversation to the top
          const [updatedCompany] = updatedCompanies.splice(updatedIndex, 1);
          return [updatedCompany, ...updatedCompanies];
        } else {
          // Transform new conversation
          const user = {};
          newConversation.participants?.forEach((participant) => {
            const { role, userId } = participant;
            if (role === "Admin") {
              user["Admin"] = {
                _id: userId._id,
                name: userId.name,
                email: userId.email,
                profileImg: userId.profileImg,
                role,
              };
            } else if (role === "Supplier") {
              user["Receiver"] = {
                _id: userId._id,
                name: userId.businessName,
                email: userId.businessEmail,
                profileImg: userId.profileImg,
                role,
              };
            } else if (role === "User") {
              user["Receiver"] = {
                _id: userId._id,
                name: `${userId.firstname} ${userId.lastname} `,
                email: userId.email,
                profileImg: userId.profileImg,
                role,
              };
            } else if (role === "Creator") {
              user["Receiver"] = {
                _id: userId._id,
                name: `${userId.username} `,
                email: userId.email,
                profileImg: userId.profileImg,
                role,
              };
            } else {
              user["Receiver"] = {
                _id: userId._id,
                name: userId.name,
                email: userId.email,
                profileImg: userId.profileImg,
                role,
              };
            }
          });

          const transformedConversation = {
            ...newConversation,
            user,
          };

          // Add new conversation to the top
          return [transformedConversation, ...prev];
        }
      });
    });

    return () => {
      socket.off("newConversation");
      socket.emit("leaveRoom", { room: "adminRoom" });
    };
  }, [socket]);

  const fetchAllCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await getAllConversation("");
      if (response.data.status === "success") {
        const data = response.data.data;
        setAllCompanies(transformedData(data));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const transformedData = (data) => {
    return data?.map((conversation) => {
      const user = {};

      conversation.participants.forEach((participant) => {
        const { role, userId } = participant;

        if (role === "Admin") {
          user["Admin"] = {
            _id: userId._id,
            name: userId.name,
            email: userId.email,
            profileImg: userId.profileImg,
            role,
          };
        } else if (role === "Supplier") {
          user["Receiver"] = {
            _id: userId._id,
            name: userId.businessName,
            email: userId.businessEmail,
            profileImg: userId.profileImg,
            role,
          };
        } else if (role === "User") {
          user["Receiver"] = {
            _id: userId._id,
            name: `${userId.firstname} ${userId.lastname} `,
            email: userId.email,
            profileImg: userId.profileImg,
            role,
          };
        } else if (role === "Creator") {
          user["Receiver"] = {
            _id: userId._id,
            name: `${userId.username} `,
            email: userId.email,
            profileImg: userId.profileImg,
            role,
          };
        } else {
          user["Receiver"] = {
            _id: userId._id,
            name: userId.name,
            email: userId.email,
            profileImg: userId.profileImg,
            role,
          };
        }
      });

      return {
        ...conversation,
        user,
      };
    });
  };

  return (
    <Stack spacing={2}>
      {/* Display loading spinner if data is being fetched */}
      {isLoading ? (
        <CircularProgress size={24} sx={{ alignSelf: "center" }} />
      ) : isLoading === false && allCompanies?.length === 0 ? (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{
            textAlign: "center",
            padding: "20px",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h6" color={appcolors.primary} fontWeight="bold">
            {t("noConversationsFound")}
          </Typography>
          <Typography variant="body2" color={appcolors.lightGrey}>
            {t("noConversationsDescription")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchAllCompanies()}
            sx={{ marginTop: "10px" }}
          >
            {t("refresh")}
          </Button>
        </Stack>
      ) : (
        <List
          sx={{
            height: "60vh",
            overflowX: "auto",
          }}
        >
          {allCompanies
            ?.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
            ?.map((val, index) => (
              <>
                <Typography fontSize={12} p={"0px 16px"} mt={1}>
                  {val?.user?.Receiver?.role}
                </Typography>
                <ListItem
                  key={index}
                  divider
                  sx={{
                    py: 0,
                    cursor: "pointer",
                    backgroundColor:
                      val._id == active?._id ? "#615EF00F" : "transparent",
                    "&:hover": {
                      backgroundColor: "#615EF00F",
                    },
                  }}
                  onClick={() =>
                    dispatch(
                      setSelectedChat({
                        _id: val?._id,
                        name: val.user.Receiver.name,
                        receiverId: val.user.Receiver._id,
                        image: val.user.Receiver.profileImg,
                        conversationId: val.conversationId,
                        senderRole: "Admin",
                        receiverRole: val.user.Receiver.role,
                        type: `AdminWith${val.user.Receiver.role}`,
                      }),
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      src={
                        val?.user?.Receiver?.profileImg || "/default-avatar.png"
                      }
                      sx={{ borderRadius: "20%" }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ mt: "0px !important" }}
                    primary={
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        width={"100%"}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={"bold"}
                          flex={4}
                          noWrap
                        >
                          {val?.user?.Receiver?.name}
                        </Typography>
                        <Typography fontSize={"0.75rem"} noWrap>
                          {moment(val?.lastUpdated).format("hh:mm a")}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Stack
                        gap={0.5}
                        direction={"row"}
                        justifyContent={"space-between"}
                      >
                        <Typography variant="body2" color={"#7c7c7c"} noWrap>
                          {val.lastMessage || t("noMessagesYet")}
                        </Typography>

                        {val.unreadMessagesCount[userData?._id] > 0 && (
                          <Chip
                            label={val.unreadMessagesCount[userData?._id] || 0}
                            sx={{
                              color: "white",
                              height: "auto",
                              backgroundColor: "primary.main",
                              "& .MuiChip-label": {
                                padding: "2px 6px 2px 6px",
                              },
                            }}
                          />
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              </>
            ))}
        </List>
      )}
    </Stack>
  );
}

export default CompanyLists;
